import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Table, Button, Modal, Form, Input, DatePicker, Select } from "antd";
import type { ColumnsType } from "antd/es/table";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import { all_routes } from "../router/all_routes";
import { usePermission } from "../../core/api/useRolePermission";
import { useAuthToken } from "../../core/data/redux/useAuthToken";
import { API_BASE_PATH } from "../../environment";

interface Holiday {
  id: number;
  holiday_title: string;
  holiday_date: string;
  descriptions: string;
  status: string;
}

const Holidays: React.FC = () => {
  const empToken = useAuthToken();
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const routes = all_routes;

  // Permission matrix for Holidays resource (changed from "Holiday" to "Holidays")
  const canCreateHoliday = usePermission("Holidays", "create");
  const canUpdateHoliday = usePermission("Holidays", "write");
  const canDeleteHoliday = usePermission("Holidays", "delete");
  const canReadHoliday = usePermission("Holidays", "read");

  // Debug permissions
  useEffect(() => {}, [
    canReadHoliday,
    canCreateHoliday,
    canUpdateHoliday,
    canDeleteHoliday,
  ]);

  const fetchHolidays = async () => {
    if (!canReadHoliday) {
      toast.warning("You don't have permission to view holidays.");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_PATH}/holiday/`, {
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${empToken}`,
        },
      });
      const sortedData = response.data.sort(
        (a: Holiday, b: Holiday) =>
          new Date(a.holiday_date).getTime() -
          new Date(b.holiday_date).getTime()
      );
      setHolidays(sortedData);
    } catch (error) {
      console.error("Error fetching holidays:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrEditHoliday = async () => {
    if (editingHoliday && !canUpdateHoliday) {
      toast.warning("You don't have permission to update holidays.");
      return;
    }
    if (!editingHoliday && !canCreateHoliday) {
      toast.warning("You don't have permission to create holidays.");
      return;
    }

    try {
      const values = await form.validateFields();

      const payload = {
        holiday_title: values.holiday_title,
        holiday_date: dayjs(values.holiday_date).format("YYYY-MM-DD"),
        descriptions: values.descriptions,
        status: values.status,
      };

      if (editingHoliday) {
        await axios.put(
          `${API_BASE_PATH}/holiday/${editingHoliday.id}`,
          payload,
          {
            headers: {
              accept: "application/json",
              Authorization: `Bearer ${empToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        toast.success("Holiday updated successfully!");
      } else {
        await axios.post(`${API_BASE_PATH}/holiday/`, payload, {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${empToken}`,
            "Content-Type": "application/json",
          },
        });
        toast.success("Holiday added successfully!");
      }

      form.resetFields();
      setModalVisible(false);
      setEditingHoliday(null);
      fetchHolidays();
    } catch (error) {
      console.error("Submit error:", error);
    }
  };

  const handleEdit = (record: Holiday) => {
    if (!canUpdateHoliday) {
      toast.warning("You don't have permission to update holidays.");
      return;
    }
    setEditingHoliday(record);
    form.setFieldsValue({
      holiday_title: record.holiday_title,
      holiday_date: dayjs(record.holiday_date),
      descriptions: record.descriptions,
      status: record.status,
    });
    setModalVisible(true);
  };

  const handleDelete = async () => {
    if (!canDeleteHoliday) {
      toast.warning("You don't have permission to delete holidays.");
      setDeleteModalVisible(false);
      setDeleteId(null);
      return;
    }

    if (deleteId !== null) {
      try {
        await axios.delete(`${API_BASE_PATH}/holiday/${deleteId}`, {
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${empToken}`,
          },
        });
        toast.success("Holiday deleted successfully!");
        fetchHolidays();
      } catch (error) {
        console.error("Delete error:", error);
      } finally {
        setDeleteModalVisible(false);
        setDeleteId(null);
      }
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, [canReadHoliday, empToken]);

  const columns: ColumnsType<Holiday> = [
    {
      title: "Title",
      dataIndex: "holiday_title",
      render: (text: string) => (
        <h6 className="fw-medium">
          <Link to="#">{text}</Link>
        </h6>
      ),
    },
    {
      title: "Date",
      dataIndex: "holiday_date",
    },
    {
      title: "Description",
      dataIndex: "descriptions",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => (
        <span className="badge badge-success d-inline-flex align-items-center badge-sm">
          <i className="ti ti-point-filled me-1" />
          {text}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="action-icon d-inline-flex">
          <Button
            type="link"
            onClick={() => handleEdit(record)}
            className="me-2 text-primary"
            // disabled={!canUpdateHoliday}
          >
            <i className="ti ti-edit" />
          </Button>
          <Button
            type="link"
            danger
            onClick={() => {
              setDeleteId(record.id);
              setDeleteModalVisible(true);
            }}
            // disabled={!canDeleteHoliday}
          >
            <i className="ti ti-trash" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Holidays</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Employee</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Holidays
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <Button
                  className="btn btn-primary d-flex align-items-center"
                  onClick={() => {
                    if (!canCreateHoliday) {
                      toast.warning(
                        "You don't have permission to create holidays."
                      );
                      return;
                    }
                    setEditingHoliday(null);
                    form.resetFields();
                    setModalVisible(true);
                  }}
                  // disabled={!canCreateHoliday}
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add Holiday
                </Button>
              </div>
              <div className="ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>

          {/* Table Card */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Holidays List</h5>
            </div>
            <div className="card-body p-0">
              <Table
                columns={columns}
                dataSource={holidays}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025 © IATT.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              IATT
            </Link>
          </p>
        </div>
      </div>

      {/* Add/Edit Holiday Modal */}
      <Modal
        title={editingHoliday ? "Edit Holiday" : "Add Holiday"}
        open={modalVisible}
        centered
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="reset" onClick={() => form.resetFields()}>
            Reset
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleAddOrEditHoliday}
            disabled={editingHoliday ? !canUpdateHoliday : !canCreateHoliday}
          >
            {editingHoliday ? "Update" : "Add"}
          </Button>,
        ]}
        zIndex={9999} // make sure modal is above sidebar
        maskStyle={{
          backgroundColor: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(4px)", // 🔥 blur effect
        }}
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            label="Title"
            name="holiday_title"
            rules={[{ required: true, message: "Please enter holiday title" }]}
          >
            <Input placeholder="Enter title" />
          </Form.Item>

          <Form.Item
            label="Date"
            name="holiday_date"
            rules={[{ required: true, message: "Please select date" }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label="Description"
            name="descriptions"
            rules={[{ required: true, message: "Please enter description" }]}
          >
            <Input.TextArea rows={3} placeholder="Enter description" />
          </Form.Item>

          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: "Please select status" }]}
          >
            <Select placeholder="Select status">
              <Select.Option value="Active">Active</Select.Option>
              <Select.Option value="Inactive">Inactive</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Yes"
        cancelText="No"
        confirmLoading={loading}
        okButtonProps={{ disabled: !canDeleteHoliday }}
      >
        <p>Are you sure you want to delete this holiday?</p>
      </Modal>
    </>
  );
};

export default Holidays;
